/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { isScopeNavigatorTask } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { pxToRem } from '@/Client/Styles/pxToRem';
import type { ScanRootOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { CloseIcon } from '@kaiizen/kai-shiki/react';

type ApprovalStatus = 'approved' | 'editRequested' | 'editResolved' | 'needsReview';

export type CloseoutApprovalSummary = {
  address?: string;
  totalTaskCount: number;
  approvedTaskCount: number;
  unassignedPhotoCount: number;
  needsEditTaskCount: number;
  attentionTaskCount: number;
};

const OVERLAY_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_OVERLAY_STYLE;
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(29, 29, 29, 0.36);
`;

const MODAL_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_STYLE;
  flex: 0 1 auto;
  width: 760px;
  max-width: calc(100vw - 96px);
  max-height: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #d6d0c8;
  border-radius: 18px;
  background: #fff;
  color: #1d1d1d;
  box-shadow: 0 24px 60px rgba(29, 29, 29, 0.28);

  @media (max-width: 856px) {
    max-width: calc(100vw - 32px);
  }

  @media (max-height: 532px) {
    max-height: calc(100vh - 32px);
  }
`;

const HEADER_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_HEADER_STYLE;
  position: relative;
  padding: 30px 28px 14px;
  border-bottom: 1px solid #d6d0c8;
`;

const EYEBROW_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_EYEBROW_STYLE;
  color: #8a847c;
  font-size: ${pxToRem(10)};
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1.4;
  text-transform: uppercase;
`;

const TITLE_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_TITLE_STYLE;
  margin: 8px 44px 2px 0;
  color: #1d1d1d;
  font-size: ${pxToRem(24)};
  font-weight: 800;
  line-height: 1.25;
`;

const ADDRESS_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_ADDRESS_STYLE;
  color: #5d5852;
  font-size: ${pxToRem(13)};
  font-weight: 600;
  line-height: 1.385;
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_CLOSE_BUTTON_STYLE;
  position: absolute;
  top: 24px;
  right: 24px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #c7bfb5;
  border-radius: 999px;
  background: #f8f5f0;
  color: #625c55;
  cursor: pointer;

  &:hover {
    background: #eee9e2;
    color: #1d1d1d;
  }
`;

const BODY_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_BODY_STYLE;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 22px 28px 16px;
`;

const STATS_GRID_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_STATS_GRID_STYLE;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
`;

const STAT_CARD_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_STAT_CARD_STYLE;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  padding: 12px 14px;
  border: 1px solid #d9d1c7;
  border-radius: 10px;
  background: #f3f0ec;
`;

const STAT_VALUE_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_STAT_VALUE_STYLE;
  font-size: ${pxToRem(26)};
  font-weight: 800;
  line-height: 1.077;
`;

const STAT_LABEL_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_STAT_LABEL_STYLE;
  color: #3c3834;
  font-size: ${pxToRem(11)};
  font-weight: 600;
  line-height: 1.273;
`;

const PANEL_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_PANEL_STYLE;
  border: 1px solid #d9d1c7;
  border-radius: 10px;
  background: #f3f0ec;
  color: #5d5852;
  padding: 14px 16px;
  font-size: ${pxToRem(13)};
  line-height: 1.231;
`;

const WARNING_PANEL_STYLE = css`
  ${PANEL_STYLE}
  margin-bottom: 18px;
  border-color: #dfab72;
  background: #fff2e2;
`;

const WARNING_TITLE_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_WARNING_TITLE_STYLE;
  margin-bottom: 10px;
  color: #bd6a42;
  font-size: ${pxToRem(14)};
  font-weight: 800;
  line-height: 1.286;
`;

const LIST_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_LIST_STYLE;
  margin: 0;
  padding-left: 16px;
`;

const CHECKLIST_STYLE = css`
  ${PANEL_STYLE}
  margin-bottom: 0;
`;

const FOOTER_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_FOOTER_STYLE;
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.75fr;
  gap: 10px;
  padding: 0 28px 16px;
`;

const FOOTER_BUTTON_STYLE = css`
  label: CLOSEOUT_APPROVAL_MODAL_FOOTER_BUTTON_STYLE;
  height: 32px;
  border: 1px solid #bfb7ad;
  border-radius: 999px;
  background: #fff;
  color: #1d1d1d;
  font-size: ${pxToRem(12)};
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f8f5f0;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.72;
  }
`;

const PRIMARY_BUTTON_STYLE = css`
  border-color: #60956b;
  background: #60956b;
  color: #fff;
  font-weight: 800;

  &:hover:not(:disabled) {
    background: #56865f;
  }
`;

const getLatestProgressStatus = (task: ScanTaskOptimized): string | undefined => {
  const updates = task.progressUpdates;
  if (!updates?.length) return undefined;
  return [...updates].sort((a, b) => {
    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return bTime - aTime;
  })[0]?.status;
};

// Only tasks that actually count toward scope (isScopeNavigatorTask: scoped by
// cost, or user-added) are included - matches the same task set ScopeNavigator
// and AdminScanPageNew treat as "real" tasks elsewhere in the app.
const getTasks = (scanRoot: ScanRootOptimized | undefined): ScanTaskOptimized[] =>
  (scanRoot?.groups?.flatMap(group => group.tasks ?? []) ?? []).filter(isScopeNavigatorTask);

const getAttentionTaskCount = (
  tasks: readonly ScanTaskOptimized[],
  approvalStatusByTaskId: Record<string, ApprovalStatus | undefined>,
): number => {
  const attentionTaskIds = new Set<string>();
  for (const task of tasks) {
    if (getLatestProgressStatus(task) !== 'completed') attentionTaskIds.add(task.id);
    if (approvalStatusByTaskId[task.id] === 'editRequested') attentionTaskIds.add(task.id);
  }
  return attentionTaskIds.size;
};

const getCountLabel = (count: number, singular: string, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

const getTaskAttentionLabel = (count: number) =>
  `${getCountLabel(count, 'task')} ${count === 1 ? 'is' : 'are'} still in progress or ${count === 1 ? 'needs' : 'need'} edit`;

export const buildCloseoutApprovalSummary = ({
  address,
  approvalStatusByTaskId = {},
  scanRoot,
}: {
  address?: string;
  approvalStatusByTaskId?: Record<string, ApprovalStatus | undefined>;
  scanRoot?: ScanRootOptimized;
}): CloseoutApprovalSummary => {
  const tasks = getTasks(scanRoot);
  return {
    address,
    totalTaskCount: tasks.length,
    approvedTaskCount: tasks.filter(task => approvalStatusByTaskId[task.id] === 'approved').length,
    unassignedPhotoCount: scanRoot?.photos?.length ?? 0,
    needsEditTaskCount: tasks.filter(task => approvalStatusByTaskId[task.id] === 'editRequested').length,
    attentionTaskCount: getAttentionTaskCount(tasks, approvalStatusByTaskId),
  };
};

const StatCard = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) => (
  <div css={STAT_CARD_STYLE}>
    <div css={STAT_VALUE_STYLE} style={{ color }}>{value}</div>
    <div css={STAT_LABEL_STYLE}>{label}</div>
  </div>
);

export const CloseoutApprovalModal = ({
  approving = false,
  summary,
  onApprove,
  onCancel,
  onRequestEdit,
}: {
  approving?: boolean;
  summary: CloseoutApprovalSummary;
  onApprove: () => void | Promise<void>;
  onCancel: () => void;
  onRequestEdit: () => void;
}) => (
  <div css={OVERLAY_STYLE} data-testid='closeout-approval-modal-overlay'>
    <section css={MODAL_STYLE} role='dialog' aria-modal='true' aria-labelledby='closeout-approval-title' data-testid='closeout-approval-modal'>
      <header css={HEADER_STYLE}>
        <button type='button' css={CLOSE_BUTTON_STYLE} aria-label='Close approval modal' onClick={onCancel}>
          <CloseIcon size={16} color='currentColor' />
        </button>
        <div css={EYEBROW_STYLE}>PROJECT CLOSEOUT</div>
        <h2 id='closeout-approval-title' css={TITLE_STYLE}>Approve closeout?</h2>
        {summary.address ? <div css={ADDRESS_STYLE}>{summary.address}</div> : null}
      </header>

      <div css={BODY_STYLE}>
        <div css={STATS_GRID_STYLE}>
          <StatCard value={summary.totalTaskCount} label='Total tasks' color='#1d1d1d' />
          <StatCard value={summary.approvedTaskCount} label='Approved' color='#60956b' />
          <StatCard value={summary.unassignedPhotoCount} label='Unassigned photos' color='#2f7da1' />
          <StatCard value={summary.needsEditTaskCount} label='Needs edit' color='#c56a43' />
        </div>

        <div css={WARNING_PANEL_STYLE}>
          <div css={WARNING_TITLE_STYLE}>Review before approving</div>
          <ul css={LIST_STYLE}>
            <li>{getCountLabel(summary.unassignedPhotoCount, 'unassigned photo')} remain</li>
            <li>{getTaskAttentionLabel(summary.attentionTaskCount)}</li>
            <li>Approval will mark the project ready for closeout</li>
          </ul>
        </div>

        <div css={CHECKLIST_STYLE}>
          <div>✓ Scope reviewed across selected rooms</div>
          <div>✓ Photo evidence and task totals included in final record</div>
        </div>
      </div>

      <footer css={FOOTER_STYLE}>
        <button type='button' css={FOOTER_BUTTON_STYLE} onClick={onCancel}>Cancel</button>
        <button type='button' css={FOOTER_BUTTON_STYLE} onClick={onRequestEdit}>Request edit</button>
        <button type='button' css={[FOOTER_BUTTON_STYLE, PRIMARY_BUTTON_STYLE]} onClick={onApprove} disabled={approving}>
          {approving ? 'Approving…' : 'Approve Closeout'}
        </button>
      </footer>
    </section>
  </div>
);
